<template>
  <div class="personal-info">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h2>{{ $t('profile.personalInfo') }}</h2>
      </div>
    </div>

    <a-row :gutter="24">
      <!-- 左侧：基本信息 -->
      <a-col :span="16">
        <a-card :title="$t('profile.basicInfo')" :bordered="false" class="info-card">
          <a-form
            ref="formRef"
            :model="formData"
            :rules="formRules"
            :label-col="{ span: 6 }"
            :wrapper-col="{ span: 18 }"
            @finish="handleSubmit"
          >
            <a-row :gutter="16">
              <a-col :span="12">
                <a-form-item :label="$t('user.name')" name="name">
                  <a-input v-model:value="formData.name" :placeholder="$t('user.enterName')" />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item :label="$t('user.nameRemarkLabel')" name="name_remark">
                  <a-input v-model:value="formData.name_remark" :placeholder="$t('user.nameRemarkPlaceholder')" />
                </a-form-item>
              </a-col>
            </a-row>

            <a-row :gutter="16">
              <a-col :span="12">
                <a-form-item :label="$t('user.nationalityLabel')" name="nationality">
                  <a-select v-model:value="formData.nationality" :placeholder="$t('user.selectNationality')" allow-clear>
                    <a-select-option 
                      v-for="nationality in nationalityOptions" 
                      :key="nationality" 
                      :value="nationality"
                    >
                      {{ nationality }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item :label="$t('user.language')" name="language">
                  <a-select v-model:value="formData.language" :placeholder="$t('user.selectLanguage')" allow-clear>
                    <a-select-option 
                      v-for="option in languageOptions" 
                      :key="option.value" 
                      :value="option.value"
                    >
                      {{ option.label }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
            </a-row>

            <a-row :gutter="16">
              <a-col :span="12">
                <a-form-item :label="$t('user.schoolLabel')" name="school">
                  <a-input v-model:value="formData.school" :placeholder="$t('user.schoolLabel')" disabled />
                  <div style="font-size: 12px; color: #999; margin-top: 4px;">
                    {{ $t('profile.schoolAssignedByAdmin') }}
                  </div>
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item :label="$t('user.departmentLabel')" name="department">
                  <a-input v-model:value="formData.department" :placeholder="$t('user.enterDepartment')" />
                </a-form-item>
              </a-col>
            </a-row>

            <a-row :gutter="16">
              <a-col :span="12">
                <a-form-item :label="$t('user.email')" name="email">
                  <a-input v-model:value="formData.email" :placeholder="$t('user.enterEmail')" />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item :label="$t('user.phoneLabel')" name="phone">
                  <a-input v-model:value="formData.phone" :placeholder="$t('user.enterPhone')" />
                </a-form-item>
              </a-col>
            </a-row>

            <!-- 管理员可见的群组信息 -->
            <a-row :gutter="16" v-if="userStore.isAdmin">
              <a-col :span="24">
                <a-form-item :label="$t('user.userGroup')" name="group_id">
                  <a-input v-model:value="groupName" disabled :placeholder="$t('user.userGroup')" />
                </a-form-item>
              </a-col>
            </a-row>


            <a-form-item :wrapper-col="{ offset: 6, span: 18 }">
              <a-space>
                <a-button type="primary" html-type="submit" :loading="loading">
                  {{ $t('profile.saveChanges') }}
                </a-button>
                <a-button @click="handleReset">
                  {{ $t('user.reset') }}
                </a-button>
              </a-space>
            </a-form-item>
          </a-form>
        </a-card>
      </a-col>

      <!-- 右侧：密码修改 -->
      <a-col :span="8">
        <!-- 密码修改 -->
        <a-card :title="$t('profile.changePassword')" :bordered="false" class="password-card">
          <a-form
            ref="passwordFormRef"
            :model="passwordData"
            :rules="passwordRules"
            @finish="handlePasswordChange"
          >
            <a-form-item :label="$t('profile.currentPassword')" name="oldPassword">
              <a-input-password 
                v-model:value="passwordData.oldPassword" 
                :placeholder="$t('profile.enterCurrentPassword')" 
                class="password-input"
              />
            </a-form-item>
            
            <a-form-item :label="$t('user.newPassword')" name="newPassword">
              <a-input-password 
                v-model:value="passwordData.newPassword" 
                :placeholder="$t('user.enterNewPassword')" 
                class="password-input"
              />
            </a-form-item>
            
            <a-form-item :label="$t('user.confirmNewPassword')" name="confirmPassword">
              <a-input-password 
                v-model:value="passwordData.confirmPassword" 
                :placeholder="$t('user.enterConfirmPassword')" 
                class="password-input"
              />
            </a-form-item>
            
            <a-form-item>
              <a-button type="primary" html-type="submit" :loading="passwordLoading" block>
                {{ $t('profile.changePassword') }}
              </a-button>
            </a-form-item>
          </a-form>
        </a-card>

        <!-- 账户信息 -->
        <a-card :title="$t('profile.accountInfo')" :bordered="false" class="account-card">
          <a-descriptions :column="1" size="small">
            <a-descriptions-item :label="$t('profile.userId')">
              {{ formData.id }}
            </a-descriptions-item>
            <a-descriptions-item :label="$t('user.username')">
              {{ formData.username }}
            </a-descriptions-item>
            <a-descriptions-item :label="$t('user.status')">
              <a-tag :color="formData.status === 'active' ? 'green' : 'red'">
                {{ getStatusText(formData.status || 'active') }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item :label="$t('profile.registerTime')">
              {{ formatDate(formData.created_at || '') }}
            </a-descriptions-item>
            <a-descriptions-item :label="$t('user.lastLoginLabel')">
              {{ formData.last_login_at ? formatDate(formData.last_login_at) : $t('profile.neverLoggedIn') }}
            </a-descriptions-item>
          </a-descriptions>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed } from 'vue'
import { message } from 'ant-design-vue'
import { useUserStore } from '@/modules/user/stores'
import { updateProfile, changePassword, type UserInfo } from '@/modules/auth/api'
import { getLanguageOptions, type LanguageOption, LanguageType } from '@/modules/system/api/language'
import dayjs from 'dayjs'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// 用户store
const userStore = useUserStore()

// 表单引用
const formRef = ref()
const passwordFormRef = ref()

// 加载状态
const loading = ref(false)
const passwordLoading = ref(false)

// 语言选项
const languageOptions = ref<LanguageOption[]>([])

// 常用国籍硬编码列表
const nationalityOptions = ref<string[]>([
  '中国', '美国', '英国', '日本', '韩国', '德国', '法国', '俄罗斯', '意大利', '西班牙',
  '加拿大', '澳大利亚', '新西兰', '巴西', '阿根廷', '墨西哥', '印度', '泰国', '越南',
  '新加坡', '马来西亚', '印度尼西亚', '菲律宾', '土耳其', '埃及', '南非', '尼日利亚',
  '沙特阿拉伯', '阿联酋', '以色列', '伊朗', '伊拉克', '阿富汗', '巴基斯坦', '孟加拉国',
  '斯里兰卡', '尼泊尔', '缅甸', '老挝', '柬埔寨', '蒙古', '哈萨克斯坦', '乌兹别克斯坦',
  '塔吉克斯坦', '吉尔吉斯斯坦', '土库曼斯坦', '阿塞拜疆', '亚美尼亚', '格鲁吉亚',
  '乌克兰', '白俄罗斯', '摩尔多瓦', '立陶宛', '拉脱维亚', '爱沙尼亚', '芬兰', '瑞典',
  '挪威', '丹麦', '冰岛', '荷兰', '比利时', '卢森堡', '瑞士', '奥地利', '捷克', '斯洛伐克',
  '波兰', '匈牙利', '罗马尼亚', '保加利亚', '克罗地亚', '塞尔维亚', '斯洛文尼亚',
  '马其顿', '阿尔巴尼亚', '黑山', '波黑', '希腊', '塞浦路斯', '马耳他', '葡萄牙',
  '爱尔兰', '冰岛', '挪威', '瑞典', '丹麦', '芬兰', '爱沙尼亚', '拉脱维亚', '立陶宛',
  '其他'
])

// 表单数据
const formData = reactive<Partial<UserInfo>>({
  id: 0,
  username: '',
  name: '',
  name_remark: '',
  nationality: '',
  language: '',
  language_micro: '',
  school: '',
  department: '',
  email: '',
  phone: '',
  user_type: 'normal',
  status: 'active',
  group_id: 0,
  created_at: '',
  last_login_at: ''
})

// 密码修改数据
const passwordData = reactive({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
})

// 群组名称（计算属性）
const groupName = computed(() => {
  // 这里应该根据group_id获取群组名称，暂时显示ID
  return formData.group_id ? t('profile.groupId', { id: formData.group_id }) : t('user.ungrouped')
})

// 表单验证规则
const formRules = computed(() => ({
  name: [
    { required: true, message: t('user.nameRequired'), trigger: 'blur' },
    { min: 2, max: 50, message: t('profile.nameLength'), trigger: 'blur' }
  ],
  email: [
    { required: true, message: t('user.emailRequired'), trigger: 'blur' },
    { type: 'email', message: t('user.emailInvalid'), trigger: 'blur' }
  ],
  phone: [
    { pattern: /^1[3-9]\d{9}$/, message: t('profile.invalidPhoneNumber'), trigger: 'blur' }
  ]
}))

// 密码验证规则
const passwordRules = computed(() => ({
  oldPassword: [
    { required: true, message: t('profile.enterCurrentPassword'), trigger: 'blur' }
  ],
  newPassword: [
    { required: true, message: t('user.enterNewPassword'), trigger: 'blur' },
    { min: 6, max: 20, message: t('profile.passwordLengthRequirement'), trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: t('user.enterConfirmPassword'), trigger: 'blur' },
    {
      validator: (_: any, value: string) => {
        if (value !== passwordData.newPassword) {
          return Promise.reject(new Error(t('user.passwordMismatchValidation')))
        }
        return Promise.resolve()
      },
      trigger: 'blur'
    }
  ]
}))

// 获取状态文本
const getStatusText = (status: string) => {
  const statusMap: Record<string, string> = {
    'active': t('user.active'),
    'inactive': t('user.inactive'),
    'suspended': t('user.suspended')
  }
  return statusMap[status] || status
}

// 格式化日期
const formatDate = (dateStr: string) => {
  return dayjs(dateStr).format('YYYY-MM-DD HH:mm:ss')
}

// 加载语言选项
const loadLanguageOptions = async () => {
  try {
    const options = await getLanguageOptions({
      language_type: LanguageType.SOURCE,
      status: true
    })
    languageOptions.value = options
  } catch (error) {
    console.error('加载语言选项失败:', error)
  }
}

// 初始化表单数据
const initFormData = () => {
  if (userStore.userInfo) {
    Object.assign(formData, userStore.userInfo)
  }
}

// 提交表单
const handleSubmit = async () => {
  try {
    loading.value = true
    await formRef.value.validate()
    
    const updateData = {
      name: formData.name,
      name_remark: formData.name_remark,
      nationality: formData.nationality,
      language: formData.language,
      school: formData.school,
      department: formData.department,
      email: formData.email,
      phone: formData.phone,
      // 保持用户的组ID，防止意外清空
      group_id: formData.group_id
    }
    
    console.log('提交的个人信息数据:', updateData)
    
    await userStore.updateProfile(updateData)
    message.success(t('profile.updateSuccess'))
  } catch (error) {
    console.error('更新个人信息失败:', error)
  } finally {
    loading.value = false
  }
}

// 重置表单
const handleReset = () => {
  initFormData()
  formRef.value?.resetFields()
}

// 修改密码
const handlePasswordChange = async () => {
  try {
    passwordLoading.value = true
    await passwordFormRef.value.validate()
    
    await userStore.changePassword(passwordData.oldPassword, passwordData.newPassword)
    message.success(t('profile.passwordChangeSuccess'))
    
    // 清空密码表单
    passwordData.oldPassword = ''
    passwordData.newPassword = ''
    passwordData.confirmPassword = ''
    passwordFormRef.value?.resetFields()
  } catch (error) {
    console.error('修改密码失败:', error)
  } finally {
    passwordLoading.value = false
  }
}

// 生命周期
onMounted(() => {
  initFormData()
  loadLanguageOptions()
})
</script>

<style lang="less" scoped>
.personal-info {
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    
    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      flex-shrink: 0;
    }
  }
}

.info-card,
.password-card,
.account-card {
  margin-bottom: 24px;
}

:deep(.ant-descriptions-item-label) {
  font-weight: 500;
  color: #666;
}

:deep(.ant-form-item-label) {
  font-weight: 500;
}

:deep(.ant-card-head-title) {
  font-weight: 600;
  color: #333;
}

/* 密码输入框等长样式 */
.password-input {
  width: 100% !important;
}

:deep(.password-input .ant-input) {
  width: 100% !important;
}

:deep(.password-input .ant-input-password) {
  width: 100% !important;
}
</style>

